// Document Picture-in-Picture: a small always-on-top window the page fills with
// its own React tree. Chrome on desktop only. Everything here is feature
// detected, so a browser without it simply has no Pop out button.

import { classifySurface, type ShareSurface } from "@breeze/core";

interface DocumentPipApi {
  requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
}

function api(): DocumentPipApi | null {
  if (typeof window === "undefined") return null;
  const candidate = (window as unknown as { documentPictureInPicture?: DocumentPipApi }).documentPictureInPicture;
  return candidate && typeof candidate.requestWindow === "function" ? candidate : null;
}

export function popoutSupported(): boolean {
  return api() !== null;
}

export function shareSurface(stream: MediaStream | null | undefined): ShareSurface | null {
  const track = stream?.getVideoTracks()[0];
  if (!track) return null;
  let raw: unknown;
  try {
    raw = (track.getSettings() as { displaySurface?: unknown }).displaySurface;
  } catch {
    raw = undefined;
  }
  return classifySurface(raw);
}

/** Gives the pop-out the app's own styles: every rule from every sheet. */
export function copyStyles(from: Document, to: Document): void {
  for (const sheet of Array.from(from.styleSheets)) {
    try {
      const style = to.createElement("style");
      style.textContent = Array.from(sheet.cssRules)
        .map((rule) => rule.cssText)
        .join("\n");
      to.head.appendChild(style);
    } catch {
      // A sheet from another origin cannot be read; link it instead.
      if (sheet.href) {
        const link = to.createElement("link");
        link.rel = "stylesheet";
        link.href = sheet.href;
        to.head.appendChild(link);
      }
    }
  }
  to.documentElement.lang = from.documentElement.lang;
}

export async function openPopoutWindow(width: number, height: number): Promise<Window> {
  const pip = api();
  if (!pip) throw new Error("Pop out is not available in this browser.");
  const win = await pip.requestWindow({ width, height });
  copyStyles(document, win.document);
  return win;
}
